import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseApiService } from '../_base-http/base-api.service';
import { NavigationItem } from '../_models/navigation-item';
import {
  CODE_PAGE_CAU_HOI_THUONG_GAP,
  CODE_PAGE_CONG_CU_DAU_TU,
  CODE_PAGE_DIEU_KHOAN_SU_DUNG,
  CODE_PAGE_DOI_NGU_LANH_DAO,
  CODE_PAGE_DOI_TAC_CUA_CHUNG_TOI,
  CODE_PAGE_GIOI_THIEU,
  CODE_PAGE_GOC_NHIN_DAU_TU,
  CODE_PAGE_HO_TRO,
  CODE_PAGE_KIEN_THUC_CO_BAN,
  CODE_PAGE_KIEN_THUC_DAU_TU,
  CODE_PAGE_LIEN_HE,
  CODE_PAGE_QUAN_HE_CO_DONG,
  CODE_PAGE_QUAN_LY_DANH_MUC_DAU_TU,
  CODE_PAGE_QUYEN_MIEN_TRU,
  CODE_PAGE_QUY_DAU_TU,
  CODE_PAGE_SU_KIEN,
  CODE_PAGE_THONG_CAO_BAO_CHI,
  CODE_PAGE_TIN_TUC,
  CODE_PAGE_TRIET_LY_DAU_TU,
  CODE_PAGE_TRUYEN_THONG,
  CODE_PAGE_TU_VAN_DAU_TU,
  CODE_PAGE_VE_CHUNG_TOI,
} from '../_models/predefined-code-list';
import { NAV_CODE_QUAN_LY_DAU_TU_HOME } from '../_models/predefined-nav-list';
import { jwtOptions } from '../_utils/common-function.util';
import { StateService } from './state.service';
@Injectable({
  providedIn: 'root',
})
export class NavigationService extends BaseApiService {
  languages?: { code: string; name: string }[] = [];
  navigationList: NavigationItem[] = [];
  navigationUrlMap: { [linkUrl: string]: NavigationItem } = {};
  navigationSlugMap: { [slug: string]: NavigationItem } = {};
  navigationCodeMap: { [code: string]: NavigationItem } = {};
  navTree: {
    item: NavigationItem;
    children: {
      item: NavigationItem;
      children?: {
        item: NavigationItem;
      }[];
    }[];
  }[] = [];
  currentLanguage = 'vi';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config: any | undefined = undefined;
  constructor(
    @Optional() @Inject(REQUEST) private request: Request,
    private _http: HttpClient,
    private _stateService: StateService,
  ) {
    super(_http, _stateService);
    this.currentLanguage = this._stateService.currentLanguage$.getValue();
    this._stateService.currentLanguage$.subscribe({
      next: (l: string) => {
        this.currentLanguage = l;
      },
    });
  }

  async getConfig() {
    if (this.config) return this.config;
    if (this.request && this.request.body.redisCache) {
      if (!this.request.body.redisCache.isOpen) await this.request.body.redisCache.connect();
      const cachedConfig = await this.request.body.redisCache.get('API-config');
      if (cachedConfig) {
        this.config = JSON.parse(cachedConfig);
      }
    }
    if (!this.config) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const config$ = this.http.get<any[]>(this.baseApiUrl + '/assets/configs.json');
      this.config = await lastValueFrom(config$);
      if (this.request && this.request.body.redisCache) {
        if (!this.request.body.redisCache.isOpen) await this.request.body.redisCache.connect();
        await this.request.body.redisCache.set('API-config', JSON.stringify(this.config));
        await this.request.body.redisCache.expire('API-config', 3600);
      }
    }
    // remoteConfig = this.config;
    // ApiAbstract.apiUrl = this.config.apiUrl;
    // ApiAbstract.identityUrl = this.config.identityUrl;
    environment.imgBaseUrl = this.config.imgBaseUrl;
    environment.fileBaseUrl = this.config.fileBaseUrl;
    environment.apiUrl = this.config.apiUrl;
    environment.apiDomain = this.config.apiDomain;
    environment.selfUrl = this.config.selfUrl;
    jwtOptions.addToDomainWhitelist(this.config.apiDomain);
    return this.config;
  }

  async getAllLanguages() {
    if (this.languages && this.languages.length > 0) return this.languages;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let languages: any[] = [];
    if (this.request && this.request.body.redisCache) {
      if (!this.request.body.redisCache.isOpen) await this.request.body.redisCache.connect();
      const cachingLanguages = await this.request.body.redisCache.get('language_list');
      if (cachingLanguages) {
        languages = JSON.parse(cachingLanguages);
      }
    }
    if (!languages || languages.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const language$ = this.http.get<any[]>(this.baseApiUrl + 'language/get-list-all-no-paging');
      languages = await lastValueFrom(language$);
      if (this.request && this.request.body.redisCache) {
        if (!this.request.body.redisCache.isOpen) await this.request.body.redisCache.connect();
        if (languages.length > 0) {
          await this.request.body.redisCache.set('language_list', JSON.stringify(languages));
          await this.request.body.redisCache.expire('language_list', 18000);
        } else {
          await this.request.body.redisCache.del('language_list');
        }
      }
    }

    this.languages = [];
    for (const l of languages) {
      if (l.status && !l.isDeleted) {
        this.languages.push({ code: l.code, name: l.name });
        if (l.isDefault) {
          this.stateService.currentLanguage$.next(l.code);
        }
      }
    }
    return this.languages;
  }

  async getAll(): Promise<NavigationItem[]> {
    if (this.request && this.request.body.redisCache) {
      if (!this.request.body.redisCache.isOpen) await this.request.body.redisCache.connect();
      const caching = await this.request.body.redisCache.get('API-All-Navigation-By-Language-' + this.currentLanguage);
      if (caching) {
        const cachingData = JSON.parse(caching);
        return cachingData;
      }
    }
    const params = this.applyQuery();
    const navigations$ = this.http.get<NavigationItem[]>(`${this.baseApiUrl}navigation/get-all-data-by-language`, { params });
    const navigations = await lastValueFrom(navigations$);
    if (this.request && this.request.body.redisCache) {
      if (!this.request.body.redisCache.isOpen) await this.request.body.redisCache.connect();
      await this.request.body.redisCache.set('API-All-Navigation-By-Language-' + this.currentLanguage, JSON.stringify(navigations));
      await this.request.body.redisCache.expire('API-All-Navigation-By-Language-' + this.currentLanguage, 18000);
    }
    return navigations;
  }

  async getNavigationForHeader() {
    // if (this.navTree.length > 0) {
    //   return this.navTree;
    // }

    const list = await this.getAll();

    this.navigationList = list.sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));
    const tree: {
      item: NavigationItem;
      children: {
        item: NavigationItem;
        children?: {
          item: NavigationItem;
        }[];
      }[];
    }[] = [];

    for (const item of list) {
      this.navigationUrlMap[item.linkUrl] = item;
      this.navigationCodeMap[item.code] = item;
      this.navigationSlugMap[item.slug] = item;
      if (item.level === 0) {
        tree.push({ item, children: [] });
      } else if (item.level === 1) {
        for (const rootItem of tree) {
          if (rootItem.item.code === item.parentCode) {
            rootItem.children.push({ item });
          } else if (rootItem.children && rootItem.children?.length > 0) {
            for (const childItem of rootItem.children) {
              if (childItem.item.code === item.parentCode) {
                if (!childItem.children) {
                  childItem.children = [];
                }
                childItem.children.push({ item });
              }
            }
          }
        }
      }
    }
    this.navTree = tree;
    this._stateService.navTree$.next(tree);
    return tree;
  }

  getListChildByNavigationSlug(query: { navigationSlug: string }) {
    const params = this.applyQuery(query);
    // if (this.navTree) {
    //   for (const item of this.navTree) {
    //     if (item.item.slug === query.navigationSlug) {
    //       const l: NavigationItem[] = [];
    //       for (const cItem of item.children) {
    //         l.push(cItem.item);
    //       }
    //       return l;
    //     }
    //   }
    // }
    return this.http.get<NavigationItem[]>(`${this.baseApiUrl}navigation/get-list-child-by-navigation-slug`, { params });
  }

  findChildNavs(code: string) {
    const l: NavigationItem[] = [];
    for (const item of this.navigationList) {
      if (item.parentCode === code) {
        l.push(item);
      }
    }
    return l;
  }

  getAboutUsNav() {
    return this.navTree.find((item) => item.item.code === CODE_PAGE_VE_CHUNG_TOI);
  }

  getAboutUsOverviewNav() {
    return this.navigationCodeMap[CODE_PAGE_GIOI_THIEU];
  }

  getAboutUsPhilosophyNav() {
    return this.navigationCodeMap[CODE_PAGE_TRIET_LY_DAU_TU];
  }

  getAboutUsLeadershipTeamNav() {
    return this.navigationCodeMap[CODE_PAGE_DOI_NGU_LANH_DAO];
  }

  getAboutUsShareholderRelationsNav() {
    return this.navigationCodeMap[CODE_PAGE_QUAN_HE_CO_DONG];
  }

  getAboutUsPartnerNav() {
    return this.navigationCodeMap[CODE_PAGE_DOI_TAC_CUA_CHUNG_TOI];
  }

  getTermsAndConditionalNav() {
    return this.navigationCodeMap[CODE_PAGE_DIEU_KHOAN_SU_DUNG];
  }

  getImmunitiesNav() {
    return this.navigationCodeMap[CODE_PAGE_QUYEN_MIEN_TRU];
  }

  getAdvisoryNav() {
    return this.navigationCodeMap[CODE_PAGE_TU_VAN_DAU_TU];
  }

  getPortfolioNav() {
    return this.navigationCodeMap[CODE_PAGE_QUAN_LY_DANH_MUC_DAU_TU];
  }

  getSupportNav() {
    return this.navTree.find((item) => item.item.code === CODE_PAGE_HO_TRO);
  }

  getContactNav() {
    return this.navigationCodeMap[CODE_PAGE_LIEN_HE];
  }

  getFaqNav() {
    return this.navigationCodeMap[CODE_PAGE_CAU_HOI_THUONG_GAP];
  }

  getCommunicationNav() {
    return this.navTree.find((item) => item.item.code === CODE_PAGE_TRUYEN_THONG);
  }

  getNewsNav() {
    return this.navigationCodeMap[CODE_PAGE_TIN_TUC];
  }

  getEventsNav() {
    return this.navigationCodeMap[CODE_PAGE_SU_KIEN];
  }

  getPressReleaseNav() {
    return this.navigationCodeMap[CODE_PAGE_THONG_CAO_BAO_CHI];
  }

  getKnowledgeNav() {
    return this.navTree.find((item) => item.item.code === CODE_PAGE_KIEN_THUC_DAU_TU);
  }

  getPerspectiveNav() {
    return this.navigationCodeMap[CODE_PAGE_GOC_NHIN_DAU_TU];
  }

  getKnowledgeBasicNav() {
    return this.navigationCodeMap[CODE_PAGE_KIEN_THUC_CO_BAN];
  }

  getKnowledgeToolsNav() {
    return this.navigationCodeMap[CODE_PAGE_CONG_CU_DAU_TU];
  }

  getInvestmentFundsNav() {
    return this.navTree.find((item) => item.item.code === CODE_PAGE_QUY_DAU_TU);
  }

  getLayoutHomeInvestmentManagements() {
    return this.navigationCodeMap[NAV_CODE_QUAN_LY_DAU_TU_HOME];
  }
}
